/** The quick actions every value on the tour record offers — the header, the
 *  "related" row, and each section's pickers, fields and rows — so the same
 *  reference gets the same set wherever it is rendered.
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 *  Id spaces (see gridLinks): /tours `trip_type=` is the tour/bus/exhibition
 *  discriminator (which IS what the header chip shows); /delivery-logs and
 *  /notifications take the tour pk as an exact int; /bookings pins it with
 *  `tour=#<id>`; /ext-ids finds a tour's mappings by `model=tour & target=<id>`.
 *  `/launch-policies?edit=` does not exist — the policy links land on the
 *  operator's list and on the tours that use it.
 */
import type { NamedRef } from '@/api/tours';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { operatorDefaultsActions } from '@/features/tour-operators/tenantLinks';
import {
  bookingsHref,
  cond,
  deliveryLogsHref,
  extIdsHref,
  featureFlagsHref,
  findReplaceHref,
  idCond,
  launchPoliciesHref,
  lineSetsHref,
  manifestConds,
  notificationsHref,
  preTripItemsHref,
  tagsHref,
  toursHref,
  vehiclesHref,
} from '@/utils/gridLinks';

export interface TourCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: TourCtx) => ctx.tenantId ?? null;

/** A tour pk on the grids that take it as an exact integer. */
const tourInt = (tourId: number) => cond('tour', String(tourId), 'exact');

// ---- Header chips ---------------------------------------------------------

export function tripTypeActions(label: string, ctx: TourCtx): ValueAction[] {
  return [act.filter(toursHref([cond('trip_type', label)], tenantOf(ctx)), `${label} tours of this operator`)];
}

export function bespokeActions(isBespoke: boolean, ctx: TourCtx): ValueAction[] {
  return [
    act.filter(
      toursHref([cond('is_bespoke', isBespoke ? 'yes' : 'no')], tenantOf(ctx)),
      isBespoke ? 'Fixed-date tours of this operator' : 'Recurring tours of this operator',
    ),
  ];
}

export function templateFlagActions(ctx: TourCtx): ValueAction[] {
  return [act.filter(toursHref([cond('use_as_template', 'yes')], tenantOf(ctx)), 'Template tours of this operator')];
}

// ---- Related records ------------------------------------------------------

export function tourBookingsActions(tourId: number, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.filter(bookingsHref([idCond('tour', tourId)], t), 'Bookings on this tour'),
    act.filter(bookingsHref(manifestConds({ tourId }), t), 'Booked only'),
  ];
}

export function tourDeliveryLogsActions(tourId: number, ctx: TourCtx): ValueAction[] {
  return [act.filter(deliveryLogsHref([tourInt(tourId)], { tenant: tenantOf(ctx) }), 'Delivery logs for this tour (7 days)')];
}

export function tourNotificationsActions(tourId: number, ctx: TourCtx): ValueAction[] {
  return [
    act.filter(notificationsHref([tourInt(tourId)], tenantOf(ctx)), 'Notifications targeting this tour'),
    act.filter(featureFlagsHref([tourInt(tourId)]), 'Feature flags scoped to this tour'),
    act.filter(findReplaceHref([tourInt(tourId)]), 'Notification texts mentioning this tour'),
  ];
}

export function tourExtIdsActions(tourId: number, ctx: TourCtx): ValueAction[] {
  return [
    act.filter(
      extIdsHref([cond('model', 'tour'), cond('target', String(tourId))], tenantOf(ctx)),
      'External-ID mappings of this tour',
    ),
  ];
}

export function templateTourActions(template: NamedRef): ValueAction[] {
  return [act.open(`/tours/${template.id}`, 'Open template tour'), act.copy(String(template.id), 'Copy template tour id')];
}

/** A URL the payload carries (map link, app deep link, KML, content links…). */
export function externalUrlActions(url: string, label = 'Open'): ValueAction[] {
  return [act.external(url, label), act.copy(url, 'Copy URL')];
}

export function startDateActions(date: string, tourId: number, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.filter(toursHref([cond('start_date', date, 'exact')], t), 'Tours starting on this date'),
    act.filter(bookingsHref([idCond('tour', tourId), cond('date', date, 'exact')], t), 'Bookings on this tour that day'),
    act.copy(date, 'Copy date'),
  ];
}

/** Created / updated: the ±delta presets on the tours grid. */
export function tourTimestampActions(ns: 'created' | 'updated', iso: string, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => toursHref([range], t), ns);
}

// ---- Launch & mapping, Basics -------------------------------------------

export function launchPolicyActions(policy: NamedRef, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(toursHref([idCond('launch_policy', policy.id)], t), 'Tours with this launch policy'),
    act.filter(launchPoliciesHref({ tenant: t }), "Operator's launch policies"),
  ];
  if (policy.name) out.push(act.copy(policy.name, 'Copy policy name'));
  return out;
}

export function launchPoliciesOfOperatorActions(ctx: TourCtx): ValueAction[] {
  return [act.filter(launchPoliciesHref({ tenant: tenantOf(ctx) }), "Operator's launch policies")];
}

/** A tour's own (system) tag — the standard tag chip set with the tour
 *  relations (tours by tag, bookings on tours with the tag). The picker only
 *  knows `{id, name}`, so there is no category to offer. */
export function systemTagActions(tag: NamedRef, ctx: TourCtx): ValueAction[] {
  return tagChipActions(
    { id: tag.id, name: tag.name ?? `#${tag.id}`, category: null },
    { tenantId: tenantOf(ctx), owner: 'tour' },
  );
}

export function latLngActions(where: { lat: number; lng: number }): ValueAction[] {
  return [act.maps(where), act.copy(`${where.lat},${where.lng}`, 'Copy coordinates')];
}

export function addressActions(address: string): ValueAction[] {
  return [act.maps(address), act.copy(address, 'Copy address')];
}

// ---- Content --------------------------------------------------------------

export function crosssellActions(tour: NamedRef, ctx: TourCtx): ValueAction[] {
  return [
    act.open(`/tours/${tour.id}`, 'Open tour'),
    act.filter(bookingsHref([idCond('tour', tour.id)], tenantOf(ctx)), 'Bookings on this tour'),
  ];
}

// ---- Itinerary / line set -------------------------------------------------

export function routeSetActions(routeSet: NamedRef, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/line-sets/${routeSet.id}`, 'Open line set'),
    act.filter(lineSetsHref({ tenant: t }), "Operator's line sets"),
    act.filter(vehiclesHref({ tenant: t }), "Operator's vehicles"),
  ];
}

export function activityActions(activity: {
  id: number;
  latitude?: number | null;
  longitude?: number | null;
}): ValueAction[] {
  const out: ValueAction[] = [act.open(`/activities/${activity.id}`, 'Open activity')];
  if (activity.latitude != null && activity.longitude != null) {
    out.push(act.maps({ lat: activity.latitude, lng: activity.longitude }));
  }
  return out;
}

// ---- Departures -----------------------------------------------------------

/** A departure slot (a tag of the operator's "departure" category): the tag,
 *  the run manifest for this tour on that slot, and its delivery logs. */
export function departureSlotActions(slot: { tagId: number; name: string }, tourId: number, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/tags/${slot.tagId}`, 'Open departure tag'),
    act.filter(bookingsHref(manifestConds({ tourId, departure: slot.name }), t), 'Booked on this departure slot'),
    act.filter(
      deliveryLogsHref([tourInt(tourId), cond('departure', slot.name)], { tenant: t }),
      'Delivery logs for this departure slot (7 days)',
    ),
    act.copy(slot.name, 'Copy time'),
  ];
}

// ---- Tag categories -------------------------------------------------------

/** A tag category on the tour. A row the tour `inherited` from its operator's defaults also
 *  offers the operator's Defaults tab, where it is changed — right after the category. */
export function tagCategoryActions(
  category: { id: number; name?: string | null; code?: string | null },
  tourId: number,
  ctx: TourCtx,
  opts: { inherited?: boolean } = {},
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [act.open(`/tag-categories/${category.id}`, 'Open category')];
  if (opts.inherited && t != null) out.push(...operatorDefaultsActions(t, 'tag-categories'));
  out.push(act.filter(tagsHref([], t, { category: category.id }), 'Tags in this category'));
  if (category.code) {
    out.push(
      act.filter(
        bookingsHref([idCond('tour', tourId), cond('category', category.code)], t),
        'Bookings on this tour with a tag in this category',
      ),
      act.copy(category.code, 'Copy category code'),
    );
  }
  return out;
}

// ---- Pre-trip items -------------------------------------------------------

export function preTripItemActions(item: { id: number }, ctx: TourCtx): ValueAction[] {
  return [
    act.open(`/pre-trip-items/${item.id}`, 'Open pre-trip item'),
    act.filter(preTripItemsHref({ tenant: tenantOf(ctx) }), "Operator's pre-trip items"),
  ];
}

export function accessGroupActions(group: { id: number }): ValueAction[] {
  return [act.open(`/rbac/access-groups/${group.id}`, 'Open access group')];
}

export function visibleUserActions(user: { id: number }): ValueAction[] {
  return [act.open(`/users/${user.id}`, 'Open user')];
}

// ---- Pre-check-in ---------------------------------------------------------

// Shared with the operator's Pre-check-in tab.
export { precheckCategoryActions, waiverActions } from '@/features/precheck/links';

// ---- Identifiers ----------------------------------------------------------

export function customIdActions(value: string, ctx: TourCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(value, 'Copy custom id'),
    act.filter(extIdsHref([cond('value', value)], t), 'External-ID mappings with this value'),
    act.filter(toursHref([cond('custom_id', value)], t), 'Tours with this custom id'),
  ];
}

export function auxCustomIdActions(value: string, ctx: TourCtx): ValueAction[] {
  return [
    act.copy(value, 'Copy external id'),
    act.filter(extIdsHref([cond('value', value), cond('kind', 'alias')], tenantOf(ctx)), 'Alias mappings with this value'),
  ];
}

export function auxIntegrationActions(
  row: { integration: string; restech_account?: string | null },
  ctx: TourCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(extIdsHref([cond('integration', row.integration)], t), 'Mappings of this integration'),
  ];
  if (row.restech_account) {
    out.push(act.filter(extIdsHref([cond('account', row.restech_account)], t), 'Mappings of this account'));
  }
  return out;
}

export function uuidActions(uuid: string): ValueAction[] {
  return [act.copy(uuid, 'Copy ext id')];
}

export function codeActions(code: string): ValueAction[] {
  return [act.copy(code, 'Copy code')];
}
